import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Loader2, Sparkles } from 'lucide-react'
import { api, APIError } from '../../api/client'
import { useB2BAuthStore } from '../../stores/useB2BAuthStore'
import ConsentCheckboxes from '../../components/ConsentCheckboxes'
import { LEGAL_VERSIONS } from '../../config/legalVersions'

/**
 * Public B2B invite acceptance page at /b2b/invite/:token.
 *
 * GET /api/b2b/invites/by-token/{token} → render workspace name + role.
 * POST /api/b2b/invites/by-token/{token}/accept → create the member,
 * receive a B2B JWT, drop the user into /b2b/dashboard.
 */
export default function InviteAcceptScreen() {
  const { token } = useParams()
  const navigate = useNavigate()
  const login = useB2BAuthStore((s) => s.login)

  const [invite, setInvite] = useState(null)
  const [error, setError] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [consent, setConsent] = useState({ terms_accepted: false, aup_accepted: false })

  useEffect(() => {
    if (!token) return
    let cancelled = false
    api.get(`/api/b2b/invites/by-token/${token}`)
      .then((data) => { if (!cancelled) setInvite(data) })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status === 410) {
          setError('This invite is no longer valid (revoked or expired).')
        } else if (err instanceof APIError && err.status === 404) {
          setError('Invite not found — check the link or ask the inviter for a new one.')
        } else {
          setError(err instanceof APIError ? (err.data?.detail || err.message) : err.message)
        }
      })
    return () => { cancelled = true }
  }, [token])

  const consentReady = consent.terms_accepted && consent.aup_accepted

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (!consentReady) {
      setError('Please accept the Business Terms and the Acceptable Use Policy to continue.')
      return
    }
    setSubmitting(true)
    try {
      const result = await api.post(`/api/b2b/invites/by-token/${token}/accept`, {
        password,
        name: name.trim() || null,
        terms_accepted: true,
        terms_version: LEGAL_VERSIONS.BUSINESS_TERMS,
        aup_accepted: true,
        aup_version: LEGAL_VERSIONS.AUP,
      })
      // Drop the new member straight into the dashboard.
      login({
        token: result.token,
        member: result.member,
        workspace: result.workspace,
      })
      navigate('/b2b/dashboard', { replace: true })
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not accept the invite.')
      setSubmitting(false)
    }
  }

  if (error) {
    return (
      <Shell>
        <div className="mt-3 text-sm text-red-600">{error}</div>
        <Link to="/b2b/auth" className="mt-6 inline-block text-sm text-accent font-bold">
          Sign in instead →
        </Link>
      </Shell>
    )
  }

  if (!invite) {
    return (
      <Shell>
        <div className="mt-3 flex items-center gap-2 text-sm text-ink-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading invite…
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <p className="text-sm text-ink-2 mt-3">
        You've been invited to join the <strong>{invite.role}</strong> role
        on workspace <strong>{invite.email}</strong>'s account.
      </p>
      <p className="mt-1 text-[11px] text-ink-3">
        Set a password to create your B2B account. You can change it later
        from Settings.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-3">Your name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            placeholder="Jane Smith"
            className="mt-2 w-full text-[15px] outline-none bg-white border border-black/10 rounded-2xl px-4 py-3 focus:border-accent transition"
          />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-3">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
            minLength={6}
            maxLength={100}
            placeholder="Min 6 characters"
            className="mt-2 w-full text-[15px] outline-none bg-white border border-black/10 rounded-2xl px-4 py-3 focus:border-accent transition"
          />
        </label>

        <ConsentCheckboxes
          mode="invite"
          values={consent}
          onChange={(key, value) => setConsent((prev) => ({ ...prev, [key]: value }))}
        />

        <button
          type="submit"
          disabled={submitting || !password || !consentReady}
          className="w-full bg-accent hover:bg-accent-hover text-white font-bold py-3 rounded-2xl transition disabled:opacity-50"
        >
          {submitting ? 'Joining…' : `Accept and join as ${invite.role}`}
        </button>
      </form>
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-card border border-black/5 p-7 max-w-md w-full">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-5 h-5 text-accent" />
          <h1 className="text-xl font-bold text-ink-1">Workspace invite</h1>
        </div>
        {children}
      </div>
    </div>
  )
}
